import type { Transaction } from "./finance.ts";
import type { FinanceCategory } from "./categories.ts";
import { brandContext, recipientBrandCandidates, type RecipientBrandMapping } from "./business-units.ts";
import type { CodeCategoryRuleLike } from "./finance.ts";

export type CategoryLearningMode = "suggest" | "auto";

export type CategoryLearningSettings = {
  mode: CategoryLearningMode;
  minimumSupport: number;
  suggestionThreshold: number;
  autoThreshold: number;
};

export const defaultCategoryLearningSettings: CategoryLearningSettings = {
  mode: "suggest",
  minimumSupport: 2,
  suggestionThreshold: 0.62,
  autoThreshold: 0.9,
};

export type CategoryEvidence =
  | "sender"
  | "recipient"
  | "description"
  | "sourceAccount"
  | "beneficiaryAccount"
  | "banks"
  | "transferType"
  | "uniqueCode";

export type CategorySuggestion = {
  category: string;
  confidence: number;
  support: number;
  averageSimilarity: number;
  evidence: CategoryEvidence[];
  autoEligible: boolean;
};

function normalized(value: string | undefined) {
  return String(value || "")
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const narrativeNoise = new Set([
  "bifast", "bi", "fast", "trf", "transfer", "dari", "from", "ke", "to",
  "debit", "debet", "credit", "kredit", "bank", "via", "ref", "reference",
]);

function narrativeTokens(value: string) {
  return new Set(normalized(value).split(" ")
    .filter((token) => token.length >= 3 && !/^\d+$/.test(token) && !narrativeNoise.has(token)));
}

function jaccard(first: Set<string>, second: Set<string>) {
  if (!first.size || !second.size) return 0;
  const intersection = [...first].filter((token) => second.has(token)).length;
  const union = first.size + second.size - intersection;
  return union ? intersection / union : 0;
}

const featureCache = new WeakMap<Transaction, ReturnType<typeof prepareFeatures>>();

function prepareFeatures(row: Transaction) {
  return {
    context: brandContext(row), sourceName: normalized(row.sourceName), beneficiaryName: normalized(row.beneficiaryName),
    sourceAccount: normalized(row.sourceAccount), beneficiaryAccount: normalized(row.beneficiaryAccount),
    sourceBank: normalized(row.sourceBank), beneficiaryBank: normalized(row.beneficiaryBank),
    transferType: normalized(row.transferType), narrative: normalized(row.subject), tokens: narrativeTokens(row.subject),
  };
}

function features(row: Transaction) {
  let value = featureCache.get(row);
  if (!value) { value = prepareFeatures(row); featureCache.set(row, value); }
  return value;
}

function compareTransactions(incoming: Transaction, example: Transaction) {
  const left = features(incoming), right = features(example);
  if (left.context !== right.context) return { score: 0, evidence: [] as CategoryEvidence[] };
  if (!incoming.direction || incoming.direction !== example.direction) return { score: 0, evidence: [] as CategoryEvidence[] };
  let score = 0;
  const evidence: CategoryEvidence[] = [];
  const add = (matches: boolean, weight: number, reason: CategoryEvidence) => {
    if (!matches) return;
    score += weight;
    evidence.push(reason);
  };

  const matches = (key: "sourceName" | "beneficiaryName" | "sourceAccount" | "beneficiaryAccount" | "sourceBank" | "beneficiaryBank" | "transferType") => Boolean(left[key] && left[key] === right[key]);
  add(matches("sourceName"), 0.27, "sender");
  add(matches("beneficiaryName"), 0.18, "recipient");
  add(matches("sourceAccount"), 0.1, "sourceAccount");
  add(matches("beneficiaryAccount"), 0.1, "beneficiaryAccount");
  add(
    matches("sourceBank") || matches("beneficiaryBank"),
    0.05,
    "banks",
  );
  add(matches("transferType"), 0.05, "transferType");
  add(Boolean(incoming.uniqueCode && incoming.uniqueCode === example.uniqueCode), 0.05, "uniqueCode");

  const incomingNarrative = left.narrative;
  const exampleNarrative = right.narrative;
  if (incomingNarrative && incomingNarrative === exampleNarrative) {
    score += 0.3;
    evidence.push("description");
  } else {
    const overlap = jaccard(left.tokens, right.tokens);
    if (overlap >= 0.25) {
      score += 0.3 * overlap;
      evidence.push("description");
    }
  }
  return { score: Math.min(1, score), evidence };
}

export function learningExamples(transactions: Transaction[], categories?: FinanceCategory[]) {
  const allowed = categories
    ? new Map(categories.filter((category) => category.active && !category.system && !category.parentSlug)
      .map((category) => [category.slug, category.kind]))
    : null;
  return transactions.filter((transaction) =>
    transaction.category !== "uncategorized"
    && !transaction.deletedAt
    && (transaction.categoryAssignmentSource === "manual" || transaction.categoryAssignmentSource === "rule")
    && Boolean(transaction.direction)
    && (!allowed || allowed.get(transaction.category) === (transaction.direction === "CREDIT" ? "income" : "cost")));
}

export function suggestCategory(
  incoming: Transaction,
  examples: Transaction[],
  settings: CategoryLearningSettings = defaultCategoryLearningSettings,
): CategorySuggestion | null {
  if (!incoming.direction || incoming.category !== "uncategorized") return null;
  const comparable = examples
    .map((example) => ({ example, ...compareTransactions(incoming, example) }))
    .filter((match) => match.score >= 0.25);
  if (!comparable.length) return null;

  const grouped = new Map<string, { weight: number; matches: typeof comparable }>();
  for (const match of comparable) {
    const current = grouped.get(match.example.category) || { weight: 0, matches: [] };
    current.weight += match.score;
    current.matches.push(match);
    grouped.set(match.example.category, current);
  }
  const ranked = [...grouped.entries()].sort(([, first], [, second]) => second.weight - first.weight);
  const [category, winner] = ranked[0];
  const totalWeight = ranked.reduce((sum, [, value]) => sum + value.weight, 0);
  const averageSimilarity = winner.weight / winner.matches.length;
  const agreement = totalWeight ? winner.weight / totalWeight : 0;
  const confidence = Math.min(0.99, agreement * (0.65 + 0.35 * averageSimilarity));
  if (winner.matches.length < settings.minimumSupport
    || confidence < settings.suggestionThreshold
    || averageSimilarity < 0.42) return null;

  const evidenceFrequency = new Map<CategoryEvidence, number>();
  winner.matches.forEach((match) => match.evidence.forEach((evidence) =>
    evidenceFrequency.set(evidence, (evidenceFrequency.get(evidence) || 0) + 1)));
  const evidencePriority: Record<CategoryEvidence, number> = {
    description: 0.3,
    sender: 0.27,
    recipient: 0.18,
    sourceAccount: 0.1,
    beneficiaryAccount: 0.1,
    banks: 0.05,
    transferType: 0.05,
    uniqueCode: 0.05,
  };
  const evidence = [...evidenceFrequency.entries()]
    .sort((first, second) => second[1] - first[1]
      || evidencePriority[second[0]] - evidencePriority[first[0]])
    .slice(0, 4)
    .map(([value]) => value);

  return {
    category,
    confidence,
    support: winner.matches.length,
    averageSimilarity,
    evidence,
    autoEligible: winner.matches.length >= Math.max(3, settings.minimumSupport)
      && confidence >= settings.autoThreshold
      && averageSimilarity >= 0.6,
  };
}

export function categorySuggestions(
  transactions: Transaction[],
  settings: CategoryLearningSettings = defaultCategoryLearningSettings,
  categories?: FinanceCategory[],
) {
  const examples = learningExamples(transactions, categories);
  const groups = new Map<string, Transaction[]>();
  for (const example of examples) {
    const key = `${features(example).context}:${example.direction}`;
    const group = groups.get(key) || [];
    group.push(example);
    groups.set(key, group);
  }
  const patterns = new Map<string, CategorySuggestion | null>();
  const suggestionFor = (row: Transaction) => {
    if (row.deletedAt || !row.direction || row.category !== "uncategorized") return null;
    const prepared = features(row);
    const key = JSON.stringify([row.direction, row.uniqueCode, prepared.context, prepared.sourceName, prepared.beneficiaryName,
      prepared.sourceAccount, prepared.beneficiaryAccount, prepared.sourceBank, prepared.beneficiaryBank, prepared.transferType, prepared.narrative]);
    if (!patterns.has(key)) patterns.set(key, suggestCategory(row, groups.get(`${prepared.context}:${row.direction}`) || [], settings));
    return patterns.get(key)!;
  };
  return new Map(transactions.map((transaction) => [
    transaction.id,
    suggestionFor(transaction),
  ]).filter((entry): entry is [string, CategorySuggestion] => Boolean(entry[1])));
}

export const learningFields = ["id", "direction", "category", "categoryAssignmentSource", "deletedAt", "businessUnit", "businessUnits", "subject", "sourceName", "beneficiaryName", "sourceAccount", "beneficiaryAccount", "sourceBank", "beneficiaryBank", "transferType", "uniqueCode"] as const satisfies readonly (keyof Transaction)[];

/** Only fields used by learning; financial/note edits do not restart inference. */
export function serializeLearningInput(transactions: Transaction[]) {
  return JSON.stringify(transactions.map((row) => ({
    id: row.id, direction: row.direction, category: row.category,
    categoryAssignmentSource: row.categoryAssignmentSource, deletedAt: row.deletedAt,
    businessUnit: row.businessUnit, businessUnits: row.businessUnits,
    subject: row.subject, sourceName: row.sourceName, beneficiaryName: row.beneficiaryName,
    sourceAccount: row.sourceAccount, beneficiaryAccount: row.beneficiaryAccount,
    sourceBank: row.sourceBank, beneficiaryBank: row.beneficiaryBank,
    transferType: row.transferType, uniqueCode: row.uniqueCode,
  })));
}

export function applyLearnedCategories(
  incoming: Transaction[],
  history: Transaction[],
  settings: CategoryLearningSettings,
  categories?: FinanceCategory[],
) {
  const examples = learningExamples(history, categories);
  const suggestions = new Map<string, CategorySuggestion>();
  const rows = incoming.map((transaction) => {
    const suggestion = suggestCategory(transaction, examples, settings);
    if (!suggestion) return transaction;
    suggestions.set(transaction.id, suggestion);
    // v0.13: learned patterns are suggestions only. Automatic decisions require
    // a separately confirmed contextual code rule.
    return transaction;
  });
  return { rows, suggestions };
}

export type CodeCategoryMemory = { category: string; brandContext: string; support: number; automatic: boolean };

export function codeCategoryMemory(incoming: Transaction, history: Transaction[], rules: CodeCategoryRuleLike[] = [], mappings: RecipientBrandMapping[] = []) {
  if (!incoming.uniqueCode || !incoming.direction) return [] as CodeCategoryMemory[];
  const context = brandContext(incoming);
  const candidates = recipientBrandCandidates(incoming, mappings);
  const matchesContext = (value: string) => context !== "unassigned" ? value === context
    : !candidates.length || value === "unassigned" || candidates.includes(value);
  const grouped = new Map<string, CodeCategoryMemory>();
  for (const row of history.filter((value) => !value.deletedAt)) {
    const confirmed = learningExamples([row]).length ? [{ uniqueCode: row.uniqueCode, direction: row.direction, brandContext: brandContext(row), category: row.category }] : [];
    const decisions = [...(row.categoryHistory || []), ...confirmed];
    const seen = new Set<string>();
    for (const decision of decisions) {
      if (decision.uniqueCode !== incoming.uniqueCode || decision.direction !== incoming.direction || !matchesContext(decision.brandContext)) continue;
      const key = `${decision.brandContext}:${decision.category}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const entry = grouped.get(key) || { category: decision.category, brandContext: decision.brandContext, support: 0, automatic: false };
      entry.support += 1;
      grouped.set(key, entry);
    }
  }
  for (const rule of rules) {
    if (rule.uniqueCode !== incoming.uniqueCode || rule.direction !== incoming.direction || !rule.brandContext || !matchesContext(rule.brandContext)) continue;
    const key = `${rule.brandContext}:${rule.category}`;
    const entry = grouped.get(key) || { category: rule.category, brandContext: rule.brandContext, support: 0, automatic: false };
    entry.automatic = rule.automatic === true;
    grouped.set(key, entry);
  }
  return [...grouped.values()].sort((a, b) => b.support - a.support || a.brandContext.localeCompare(b.brandContext) || a.category.localeCompare(b.category));
}
